import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { SERVICES } from "../lib/services";
import { usePricing } from "../lib/usePricing";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2, AlertTriangle } from "lucide-react";
import StepService from "./booking/StepService";
import StepPreferredDays, { MAX_PREFERRED_DAYS } from "./booking/StepPreferredDays";
import StepVehicle from "./booking/StepVehicle";
import StepSummary from "./booking/StepSummary";

const STEPS = ["Služba", "Termín", "Vůz a kontakt", "Shrnutí"];

export default function BookingModal({ open, onClose, prefill }) {
  const { user, register: registerAccount } = useAuth();
  const [step, setStep] = useState(0);
  const [tier, setTier] = useState(prefill?.tier || "classic");
  const [serviceType, setServiceType] = useState(prefill?.serviceType || "interior");
  const [vehicleType, setVehicleType] = useState("osobni");
  const [vehicleBrandModel, setVehicleBrandModel] = useState("");
  const [extras, setExtras] = useState([]);
  const [preferredDays, setPreferredDays] = useState([]); // [{date: "YYYY-MM-DD", part: "morning"|"afternoon"|"full"}]
  const [promoCode, setPromoCode] = useState("");
  const [promoStatus, setPromoStatus] = useState(null); // { valid, discount, message }
  const [notes, setNotes] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("online");
  const [locationType, setLocationType] = useState("studio"); // studio | mobile
  const [serviceAddress, setServiceAddress] = useState("");
  const [addressNote, setAddressNote] = useState("");
  const [utilitiesConfirmed, setUtilitiesConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitFailed, setSubmitFailed] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  // Guest contact (when not logged in)
  const [guestForm, setGuestForm] = useState({ firstName: "", lastName: "", email: "", phone: "", password: "" });
  // Set to true once the user tries to advance/submit with missing/invalid
  // fields — turns on the red highlight + hint text for empty required fields.
  const [showErrors, setShowErrors] = useState(false);

  // Reset when opened with prefill
  useEffect(() => {
    if (open) {
      if (prefill?.tier) setTier(prefill.tier);
      if (prefill?.serviceType) setServiceType(prefill.serviceType);
      setStep(0);
      setPreferredDays([]);
      setSubmitError("");
      setSubmitFailed(false);
      setSubmitSuccess(null);
      setShowErrors(false);
    }
  }, [open, prefill]);

  // Live pricing data (services, extras, surcharges) — falls back to static
  // copy from services.js if /api/pricing/public is still loading or errored.
  const { services: liveServices, additionalServices: liveExtras, vehicleTypes: liveVehicleTypes } = usePricing();
  const ADDITIONAL_SERVICES = liveExtras;
  const VEHICLE_TYPES = liveVehicleTypes;

  // Mobile service automatically adds the mobile_service surcharge — price
  // pulled live from the pricing config below.
  useEffect(() => {
    setExtras((prev) =>
      locationType === "mobile"
        ? prev.includes("mobile_service") ? prev : [...prev, "mobile_service"]
        : prev.filter((x) => x !== "mobile_service")
    );
  }, [locationType]);

  const svc = (liveServices?.[tier]?.[serviceType]) || SERVICES[tier][serviceType];
  const duration = svc.duration;
  const basePrice = svc.price;
  const mobileExtra = 400; // Dojezd nad 25 km od Zelenče pod hranicí 2 400 Kč
  const vehicleSurcharge = basePrice * (VEHICLE_TYPES.find(v => v.id === vehicleType)?.surcharge || 0);
  const nonTravelExtrasTotal = extras.filter((e) => e !== "mobile_service").reduce((sum, e) => sum + (ADDITIONAL_SERVICES.find(a => a.id === e)?.price || 0), 0);
  const subtotalBeforeTravel = basePrice + vehicleSurcharge + nonTravelExtrasTotal;
  const travelFee = (locationType === "mobile" && subtotalBeforeTravel < 2400) ? mobileExtra : 0;
  const extrasTotal = nonTravelExtrasTotal + travelFee;
  const subtotal = basePrice + vehicleSurcharge + extrasTotal;
  const discount = promoStatus?.valid ? subtotal * (promoStatus.discount / 100) : 0;
  const total = subtotal - discount;

  const next = () => {
    if (!valid) { setShowErrors(true); return; }
    setShowErrors(false);
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };
  const prev = () => { setShowErrors(false); setStep(s => Math.max(s - 1, 0)); };

  const validatePromo = async () => {
    if (!promoCode) return;
    if (!user) {
      setPromoStatus({ valid: false, error: "Pro promo kód se prosím přihlaste" });
      return;
    }
    try {
      const { data } = await api.get(`/promo/validate/${encodeURIComponent(promoCode)}`);
      setPromoStatus({ valid: true, discount: data.discount, message: data.message });
    } catch (e) {
      setPromoStatus({ valid: false, error: e?.response?.data?.detail || "Neplatný kód" });
    }
  };

  const submit = async () => {
    setSubmitting(true); setSubmitError(""); setSubmitFailed(false);
    try {
      // If guest (no account yet), register first — the freshly issued token
      // is written to localStorage BEFORE the booking call below, so axios'
      // request interceptor (lib/api.js) automatically attaches it. No page
      // reload is needed (and a reload here previously dropped the booking
      // request entirely — the #1 reported "first booking never submits" bug).
      if (!user) {
        if (!guestForm.email || !guestForm.password || guestForm.password.length < 6 || !guestForm.firstName || !guestForm.lastName) {
          throw new Error("Vyplňte prosím všechny kontaktní údaje (heslo musí mít alespoň 6 znaků)");
        }
        if (!guestForm.phone || guestForm.phone.trim().length < 6) {
          throw new Error("Vyplňte prosím telefonní číslo (potřebujeme ho pro domluvení termínu)");
        }
        await registerAccount({
          email: guestForm.email,
          password: guestForm.password,
          firstName: guestForm.firstName,
          lastName: guestForm.lastName,
          phone: guestForm.phone,
        });
      }

      const payload = {
        serviceType,
        serviceTier: tier,
        vehicleType,
        vehicleBrandModel,
        preferredDays,
        additionalServices: extras,
        promoCode: promoStatus?.valid ? promoCode.toUpperCase() : null,
        notes: notes || null,
        paymentMethod,
        locationType,
        serviceAddress: locationType === "mobile" ? serviceAddress.trim() : null,
        addressNote: locationType === "mobile" ? (addressNote.trim() || null) : null,
      };
      const { data } = await api.post("/bookings/create", payload);
      // Rezervace je uložená na serveru → teprve teď provedeme skutečnou
      // navigaci na /dekujeme (nové načtení stránky kvůli měření konverzí
      // v Google Ads). ID předáme přes sessionStorage.
      try { sessionStorage.setItem("apex_last_booking_id", data?.booking_id || ""); } catch { /* ignore */ }
      window.location.href = "/dekujeme";
      return; // spinner zůstává aktivní až do přechodu na novou stránku
    } catch (e) {
      setSubmitError(e?.response?.data?.detail || e.message || "Nepodařilo se vytvořit rezervaci");
      setSubmitFailed(true);
      setSubmitting(false);
    }
  };

  const handleSubmitClick = () => {
    if (!valid) { setShowErrors(true); return; }
    submit();
  };

  const guestPasswordOk = !!(guestForm.password && guestForm.password.length >= 6);
  const guestPhoneOk = !!(guestForm.phone && guestForm.phone.trim().length >= 6);

  const valid = (() => {
    if (step === 0) return !!tier && !!serviceType;
    if (step === 1) return preferredDays.length > 0;
    if (step === 2) {
      const locationOk = locationType !== "mobile" || (serviceAddress.trim().length > 3 && utilitiesConfirmed);
      return !!vehicleBrandModel.trim() && locationOk && (user || (guestForm.email && guestPasswordOk && guestPhoneOk && guestForm.firstName && guestForm.lastName));
    }
    return true;
  })();

  return (
    <Modal open={open} onClose={onClose} title="Rezervace termínu" size="lg" testId="booking-modal">
      {/* Stepper */}
      <div className="flex items-center justify-between mb-8">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                i < step ? "bg-apex-gold text-apex-bg" : i === step ? "bg-apex-gold text-apex-bg ring-4 ring-apex-gold/20" : "bg-apex-card text-apex-cream/50 border border-apex-line"
              }`}>
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`text-xs hidden md:inline ${i <= step ? "text-apex-cream" : "text-apex-cream/45"}`}>{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-2 ${i < step ? "bg-apex-gold" : "bg-apex-line"}`} />}
          </React.Fragment>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {submitSuccess ? (
          <motion.div key="success" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="text-center py-8" data-testid="booking-success">
            <div className="w-16 h-16 rounded-full bg-emerald-500/15 text-emerald-300 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8" />
            </div>
            <h3 className="font-display text-2xl text-apex-cream font-bold">Žádost o rezervaci přijata</h3>
            <p className="mt-3 text-apex-cream/75 max-w-md mx-auto">
              {paymentMethod === "cash"
                ? <>Vaše rezervace čeká na potvrzení naším týmem (do 1 hodiny). Jakmile ji potvrdíme, přijde vám e-mail — poté už stačí jen dorazit na termín a <strong className="text-apex-gold">zaplatit na místě</strong>.</>
                : <>Vaše rezervace čeká na potvrzení naším týmem (do 1 hodiny). Jakmile ji potvrdíme, přijde vám e-mail — pak už jen zaplatíte online v <strong className="text-apex-gold">Mé rezervace</strong>, nebo na místě.</>}
            </p>
            <div className="mt-4 inline-block px-5 py-3 rounded-xl bg-apex-card border border-apex-line text-apex-cream/80">
              ID rezervace: <span className="text-apex-gold font-mono">{submitSuccess.booking_id}</span>
            </div>
            <div className="mt-6">
              <button onClick={onClose} className="btn-gold">Zavřít</button>
            </div>
          </motion.div>
        ) : submitFailed ? (
          <motion.div key="failed" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="text-center py-8" data-testid="booking-failed">
            <div className="w-16 h-16 rounded-full bg-red-500/15 text-red-300 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8" />
            </div>
            <h3 className="font-display text-2xl text-apex-cream font-bold">Rezervaci se nepodařilo odeslat</h3>
            <p className="mt-3 text-apex-cream/75 max-w-md mx-auto" data-testid="booking-failed-message">
              {submitError || "Zkuste to prosím znovu, nebo nás mezitím kontaktujte telefonicky."}
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <button onClick={() => setSubmitFailed(false)} className="btn-gold" data-testid="booking-retry-btn">Zkusit znovu</button>
              <button onClick={onClose} className="btn-ghost-gold" data-testid="booking-close-failed-btn">Zavřít</button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.35 }}
          >
            {step === 0 && <StepService
              tier={tier} setTier={setTier} serviceType={serviceType} setServiceType={setServiceType}
              extras={extras} setExtras={setExtras}
              services={liveServices} additionalServices={ADDITIONAL_SERVICES}
            />}
            {step === 1 && <StepPreferredDays
              preferredDays={preferredDays} setPreferredDays={setPreferredDays}
              showErrors={showErrors}
            />}
            {step === 2 && <StepVehicle
              vehicleType={vehicleType} setVehicleType={setVehicleType}
              vehicleBrandModel={vehicleBrandModel} setVehicleBrandModel={setVehicleBrandModel}
              notes={notes} setNotes={setNotes}
              locationType={locationType} setLocationType={setLocationType}
              serviceAddress={serviceAddress} setServiceAddress={setServiceAddress}
              addressNote={addressNote} setAddressNote={setAddressNote}
              utilitiesConfirmed={utilitiesConfirmed} setUtilitiesConfirmed={setUtilitiesConfirmed}
              user={user} guestForm={guestForm} setGuestForm={setGuestForm}
              vehicleTypes={VEHICLE_TYPES} mobileExtra={mobileExtra}
              showErrors={showErrors} guestPasswordOk={guestPasswordOk} guestPhoneOk={guestPhoneOk}
            />}
            {step === 3 && <StepSummary
              tier={tier} serviceType={serviceType} svc={svc} vehicleType={vehicleType} vehicleBrandModel={vehicleBrandModel}
              extras={extras} preferredDays={preferredDays} duration={duration}
              basePrice={basePrice} vehicleSurcharge={vehicleSurcharge} extrasTotal={nonTravelExtrasTotal} travelFee={travelFee} discount={discount} total={total}
              promoCode={promoCode} setPromoCode={setPromoCode} promoStatus={promoStatus} validatePromo={validatePromo}
              paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod}
              locationType={locationType} serviceAddress={serviceAddress} addressNote={addressNote}
              submitError={submitError}
              additionalServices={ADDITIONAL_SERVICES} vehicleTypes={VEHICLE_TYPES}
            />}
          </motion.div>
        )}
      </AnimatePresence>

      {!submitSuccess && !submitFailed && (
        <div className="mt-8 flex items-center justify-between gap-3 border-t border-apex-line pt-5">
          <button onClick={step === 0 ? onClose : prev} className="btn-ghost-gold !py-2.5 !px-5 text-sm" data-testid="booking-back-btn">
            <ArrowLeft className="w-4 h-4" /> {step === 0 ? "Zrušit" : "Zpět"}
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={next} className="btn-gold !py-2.5 !px-5 text-sm" data-testid="booking-next-btn">
              Pokračovat <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={handleSubmitClick} disabled={submitting} className="btn-gold !py-2.5 !px-5 text-sm disabled:opacity-50" data-testid="booking-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Odeslat žádost <Check className="w-4 h-4" /></>}
            </button>
          )}
        </div>
      )}
    </Modal>
  );
}
